import { z } from 'zod';

export const standardAnalyticsStatCategory = 'standard' as const;

/** Allowed API values (order here does not affect UI grouping). */
export const analyticsStatCategories = ['standard', 'specialised'] as const;

export const analyticsStatCategorySchema = z.enum(analyticsStatCategories);

export type AnalyticsStatCategory = z.infer<typeof analyticsStatCategorySchema>;

export function analyticsStatCategorySectionLabel(category: AnalyticsStatCategory): string {
  const titled = category.charAt(0).toUpperCase() + category.slice(1);
  return `${titled} Dimensions`;
}

/** Standard first; other categories follow (order among non-standard is not defined). */
export function splitByAnalyticsStatCategory<T>(
  items: readonly T[],
  categoryOf: (item: T) => AnalyticsStatCategory | undefined,
): Array<{ category: AnalyticsStatCategory; items: T[] }> {
  const buckets = new Map<AnalyticsStatCategory, T[]>();

  for (const item of items) {
    const category = categoryOf(item) ?? standardAnalyticsStatCategory;
    const bucket = buckets.get(category);
    if (bucket) {
      bucket.push(item);
    } else {
      buckets.set(category, [item]);
    }
  }

  const groups: Array<{ category: AnalyticsStatCategory; items: T[] }> = [];
  const standardItems = buckets.get(standardAnalyticsStatCategory);
  if (standardItems?.length) {
    groups.push({ category: standardAnalyticsStatCategory, items: standardItems });
  }

  for (const [category, groupItems] of buckets) {
    if (category !== standardAnalyticsStatCategory && groupItems.length > 0) {
      groups.push({ category, items: groupItems });
    }
  }

  return groups;
}

/** Headings are unnecessary when every item is standard. */
export function analyticsStatCategoryGroupsNeedHeadings(
  groups: ReadonlyArray<{ category: AnalyticsStatCategory }>,
): boolean {
  return groups.some((group) => group.category !== standardAnalyticsStatCategory);
}
